/**
 * merchant_hive.ts — PURE deterministic layout of the Merchant Hive city:
 * one mega hive of shops of different sizes on a flat Manhattan street grid,
 * the arrival plaza pinned clear at the origin (landOnWorld and the menu
 * teleport both drop travelers at 0,0). Same doctrine as settlements: the
 * layout is a function of the world's terrainSeed — every client rebuilds the
 * identical city, nothing is stored.
 */

export interface HiveShop {
  x: number;
  z: number;
  /** Footprint (m): width along local X, depth along local Z. */
  w: number;
  d: number;
  h: number;
  yaw: number;
  /** 0 stall · 1 shop · 2 emporium · 3 mega-hall (size class drives the look). */
  tier: 0 | 1 | 2 | 3;
  /** 0..1 — per-shop tint pick inside the tier palette. */
  hue: number;
}

export const HIVE_BLOCK_M = 64;      // street grid pitch
export const HIVE_STREET_M = 14;     // street width between block faces
export const HIVE_PLAZA_R = 84;      // arrival plaza kept clear of buildings
export const HIVE_EXTENT_M = 1792;   // city half-extent (fits the 4096 m world)

function fnv(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return h >>> 0;
}
const roll = (seed: string, salt: string): number => fnv(`${seed}|${salt}`) / 0xffffffff;

/**
 * The whole city. Each 64 m block hosts shops along its four street faces —
 * fronts flush with the street, backs toward the block core — with the size
 * class rolled per lot: mostly stalls and shops, emporiums on block corners,
 * a rare mega-hall claiming a full block face.
 */
export function hiveLayout(seed: string): HiveShop[] {
  const shops: HiveShop[] = [];
  const usable = HIVE_BLOCK_M - HIVE_STREET_M; // 50 m buildable core per block
  for (let bx = -HIVE_EXTENT_M; bx < HIVE_EXTENT_M; bx += HIVE_BLOCK_M) {
    for (let bz = -HIVE_EXTENT_M; bz < HIVE_EXTENT_M; bz += HIVE_BLOCK_M) {
      const cx = bx + HIVE_BLOCK_M / 2;
      const cz = bz + HIVE_BLOCK_M / 2;
      if (Math.hypot(cx, cz) < HIVE_PLAZA_R + usable * 0.7) continue; // the plaza stays open
      const bkey = `${bx}:${bz}`;
      // A rare mega-hall claims the whole block (denser toward the center ring).
      if (roll(seed, `${bkey}|mega`) < 0.035) {
        shops.push({
          x: cx, z: cz,
          w: usable * (0.82 + roll(seed, `${bkey}|mw`) * 0.14),
          d: usable * (0.82 + roll(seed, `${bkey}|md`) * 0.14),
          h: 15 + roll(seed, `${bkey}|mh`) * 9,
          yaw: (Math.floor(roll(seed, `${bkey}|myaw`) * 4) * Math.PI) / 2,
          tier: 3,
          hue: roll(seed, `${bkey}|mhue`),
        });
        continue;
      }
      // Otherwise: shops along TWO OPPOSITE faces (rolled N/S or E/W per
      // block) with depth clamped under half the core — overlap is impossible
      // by construction, and alternating pairs still reads as a full hive.
      const ew = roll(seed, `${bkey}|pair`) < 0.5;
      for (const face of ew ? [2, 3] : [0, 1]) {
        const fkey = `${bkey}|f${face}`;
        const n = 1 + Math.floor(roll(seed, `${fkey}|n`) * 3); // 1..3 lots per face
        for (let i = 0; i < n; i++) {
          const lkey = `${fkey}|l${i}`;
          const r = roll(seed, `${lkey}|tier`);
          const tier: HiveShop['tier'] = r < 0.5 ? 0 : r < 0.86 ? 1 : 2;
          const w = tier === 0 ? 5 + roll(seed, `${lkey}|w`) * 3
            : tier === 1 ? 10 + roll(seed, `${lkey}|w`) * 6
              : 18 + roll(seed, `${lkey}|w`) * 8;
          const d = Math.min(usable / 2 - 2, w * (0.7 + roll(seed, `${lkey}|d`) * 0.5));
          const h = tier === 0 ? 3.4 + roll(seed, `${lkey}|h`) * 1.2
            : tier === 1 ? 5.5 + roll(seed, `${lkey}|h`) * 2.5
              : 9 + roll(seed, `${lkey}|h`) * 4;
          // Position along the face, front flush with the street edge.
          const along = (i + 0.5) / n; // 0..1 across the face
          const edge = usable / 2 - d / 2; // front face sits at the block edge
          const u = (along - 0.5) * (usable - w); // slide along the face
          let x = cx, z = cz, yaw = 0;
          if (face === 0) { z = cz - edge; x = cx + u; yaw = Math.PI; }        // north face, door to -Z street
          else if (face === 1) { z = cz + edge; x = cx + u; yaw = 0; }          // south face
          else if (face === 2) { x = cx - edge; z = cz + u; yaw = Math.PI / 2; } // west face
          else { x = cx + edge; z = cz + u; yaw = -Math.PI / 2; }               // east face
          shops.push({ x, z, w, d, h, yaw, tier, hue: roll(seed, `${lkey}|hue`) });
        }
      }
    }
  }
  return shops;
}
